import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CreditCard, 
  User, 
  Mail, 
  MapPin, 
  FileText,
  DollarSign,
  Shield,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPHONE_TOKEN = process.env.REACT_APP_PAYPHONE_TOKEN || '_EwUtd6rdUY2Ss8R379J89baoFgRCXB-Eyc7TdzeWasKRaKz-QCD_y6B6HL0HEZgLwL_pZU3QYV03tIvNvf03Y_9-skRvO28Fy6YHMqJt1p4sznCwJI0oKur-jdMrz67YhKDvqz9ObtlqOiZ7rMph1HfhFZ6iMYzaDd45maUMrJyxC9l-91fi3VrXza_Xw8yiEQijL04v41OcgrBiM-z1mwkjwfpkKTz1tIRpcYOWdyyJiLuMfQcUcpUJhULdKZFFRLSdiwOwZWNbVT-q9n9djPxPn5gR5BHadgzE34VCa77dPHO5VFXFoDqCcZN8uYRuZN10A';
const PAYPHONE_STORE_ID = process.env.REACT_APP_PAYPHONE_STORE_ID || '17333fe3-433c-44af-9607-5d6272aec946';
const PAYPHONE_SURCHARGE_PCT = 5; // recargo Payphone aplicado al cliente
const PAYPHONE_FALLBACK_PHONE_EC = '0991466821'; // celular Analytica por defecto si el cliente es internacional

// Detecta si un telefono pertenece a Ecuador y lo normaliza a 10 digitos formato 09XXXXXXXX.
// Si es internacional (otro codigo de pais) o no es valido, devuelve el fallback.
const normalizePhoneEC = (raw) => {
  if (!raw) return PAYPHONE_FALLBACK_PHONE_EC;
  const trimmed = String(raw).trim();
  // Detecta codigo de pais explicito que NO sea Ecuador (+593)
  const hasCountryPrefix = /^\+/.test(trimmed);
  const onlyDigits = trimmed.replace(/[^0-9]/g, '');
  if (!onlyDigits) return PAYPHONE_FALLBACK_PHONE_EC;
  // Caso 1: comienza con 593 (codigo Ecuador) -> es ecuatoriano
  if (onlyDigits.startsWith('593')) {
    let local = onlyDigits.slice(3);
    if (!local.startsWith('0')) local = '0' + local;
    return /^09\d{8}$/.test(local) ? local : PAYPHONE_FALLBACK_PHONE_EC;
  }
  // Caso 2: tenia + pero codigo no es 593 -> internacional, fallback
  if (hasCountryPrefix) return PAYPHONE_FALLBACK_PHONE_EC;
  // Caso 3: solo digitos sin codigo
  // 10 digitos arrancando con 09 -> EC mobile valido
  if (/^09\d{8}$/.test(onlyDigits)) return onlyDigits;
  // 9 digitos arrancando con 9 -> probablemente falta el 0
  if (/^9\d{8}$/.test(onlyDigits)) return '0' + onlyDigits;
  // Cualquier otra cosa (numero corto, internacional sin +, etc.) -> fallback
  return PAYPHONE_FALLBACK_PHONE_EC;
};

// Indica si el telefono original era ecuatoriano valido (sin usar fallback)
const isEcuadorianPhone = (raw) => {
  if (!raw) return false;
  const trimmed = String(raw).trim();
  const onlyDigits = trimmed.replace(/[^0-9]/g, '');
  if (/^\+/.test(trimmed) && !onlyDigits.startsWith('593')) return false;
  if (onlyDigits.startsWith('593')) return /^09\d{8}$/.test('0' + onlyDigits.slice(3).replace(/^0/, ''));
  return /^09\d{8}$/.test(onlyDigits) || /^9\d{8}$/.test(onlyDigits);
};
const isValidPhoneEC = (p) => /^09\d{8}$/.test(normalizePhoneEC(p));

const PayphonePage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    identificacion: '',
    tipoIdentificacion: '1', // 1=Cédula, 2=RUC, 3=Pasaporte
    direccion: '',
    telefono: '',
    monto: '',          // monto BASE (lo que se debe segun el contrato)
    referencia: '',
    itemId: '',         // id de la cuota (si viene de Finanzas)
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [showPaymentBox, setShowPaymentBox] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [phoneFallbackNotice, setPhoneFallbackNotice] = useState(false);
  // Recargo Payphone: por defecto activado, pero se puede desactivar via URL (?surcharge=0)
  // o segun la config del plan financiero (apply_payphone_surcharge=false).
  const [applySurcharge, setApplySurcharge] = useState(true);
  const paymentBoxRef = useRef(null);
  const ppbInstanceRef = useRef(null);

  // Monto con recargo Payphone +5% (si esta activado)
  const baseAmount = parseFloat(formData.monto) || 0;
  const surchargePct = applySurcharge ? PAYPHONE_SURCHARGE_PCT : 0;
  const surcharge = +(baseAmount * (surchargePct / 100)).toFixed(2);
  const totalAmount = +(baseAmount + surcharge).toFixed(2);

  // Auto-completar desde URL params al cargar (link enviado desde Finanzas)
  useEffect(() => {
    const itemId = searchParams.get('item');
    const amount = searchParams.get('amount');
    const cliente = searchParams.get('cliente');
    const email = searchParams.get('email');
    const referencia = searchParams.get('referencia');
    const surchargeParam = searchParams.get('surcharge');

    const updates = {};
    if (amount) updates.monto = parseFloat(amount).toFixed(2);
    if (cliente) updates.nombre = cliente;
    if (email) updates.email = email;
    if (referencia) updates.referencia = referencia;
    if (itemId) updates.itemId = itemId;

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      setAutofilled(true);
    }

    // Override desde URL si viene explicito
    if (surchargeParam === '0') setApplySurcharge(false);
    else if (surchargeParam === '1') setApplySurcharge(true);

    // Si tenemos itemId, buscar datos adicionales del contrato
    if (itemId) {
      axios.get(`${API}/finance/public/payment-item/${itemId}`)
        .then(({ data }) => {
          const rawPhone = data.client?.phone || '';
          const normalized = normalizePhoneEC(rawPhone);
          const usedFallback = !!rawPhone && !isEcuadorianPhone(rawPhone);
          setFormData(prev => ({
            ...prev,
            nombre: prev.nombre || data.client?.company_name || data.client?.representative_name || '',
            email: prev.email || data.client?.email || '',
            telefono: prev.telefono || normalized,
            direccion: prev.direccion || data.client?.address || '',
            identificacion: prev.identificacion || data.client?.ruc || '',
            tipoIdentificacion: data.client?.ruc && data.client.ruc.length === 13 ? '2' : prev.tipoIdentificacion,
            referencia: prev.referencia || `${data.contract_number || ''} - ${data.description || ''}`.trim(),
          }));
          // Fuente de verdad final: el flag del plan. Solo aplicar si NO vino override en URL.
          if (surchargeParam !== '0' && surchargeParam !== '1' && typeof data.apply_payphone_surcharge === 'boolean') {
            setApplySurcharge(data.apply_payphone_surcharge);
          }
          if (usedFallback) {
            setPhoneFallbackNotice(true);
          }
        })
        .catch(() => {/* silencioso */});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate form
  useEffect(() => {
    const { nombre, email, identificacion, direccion, monto, telefono } = formData;
    const isValid = nombre.trim() !== '' && 
                   email.trim() !== '' && 
                   email.includes('@') &&
                   identificacion.trim() !== '' && 
                   direccion.trim() !== '' &&
                   monto !== '' && 
                   parseFloat(monto) > 0 &&
                   isValidPhoneEC(telefono);
    setIsFormValid(isValid);
  }, [formData]);

  // Load Payphone scripts
  useEffect(() => {
    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.payphonetodoesposible.com/box/v1.1/payphone-payment-box.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://cdn.payphonetodoesposible.com/box/v1.1/payphone-payment-box.js';
    script.type = 'module';
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(script);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateTransactionId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `TXN-${timestamp}-${random}`;
  };

  const handleProceedToPayment = async () => {
    if (!isFormValid) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    // Store billing data in localStorage for the confirmation page
    const billingData = {
      ...formData,
      baseAmount,
      surcharge,
      totalAmount,
      surchargePct: surchargePct,
      applySurcharge,
      monto: totalAmount.toFixed(2), // monto efectivo cobrado por Payphone
      transactionId: generateTransactionId(),
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('payphone_billing_data', JSON.stringify(billingData));

    setShowPaymentBox(true);

    // Wait for DOM update and Payphone script to be ready
    setTimeout(() => {
      initializePayphoneBox(billingData);
    }, 500);
  };

  const initializePayphoneBox = (billingData) => {
    if (typeof window.PPaymentButtonBox === 'undefined') {
      console.error('Payphone SDK not loaded');
      toast.error('Error cargando el sistema de pagos. Recargue la página.');
      return;
    }

    // Clear previous instance
    const container = document.getElementById('pp-button');
    if (container) {
      container.innerHTML = '';
    }

    // Total con recargo Payphone (+5%) en centavos
    const amountInCents = Math.round(totalAmount * 100);

    try {
      ppbInstanceRef.current = new window.PPaymentButtonBox({
        token: PAYPHONE_TOKEN,
        clientTransactionId: billingData.transactionId,
        amount: amountInCents,
        amountWithoutTax: amountInCents, // Sin impuesto por defecto
        currency: 'USD',
        storeId: PAYPHONE_STORE_ID,
        reference: billingData.referencia || `Pago Analytica - ${billingData.nombre}`,
        lang: 'es',
        defaultMethod: 'card',
        timeZone: -5,
        // Payphone exige formato E.164 (+593XXXXXXXXX) sin el 0 inicial
        phoneNumber: `+593${normalizePhoneEC(billingData.telefono).slice(1)}`,
        email: billingData.email,
        documentId: billingData.identificacion,
        identificationType: parseInt(billingData.tipoIdentificacion),
        optionalParameter: JSON.stringify({
          nombre: billingData.nombre,
          direccion: billingData.direccion
        })
      }).render('pp-button');

      toast.success('Cajita de pagos lista. Complete el pago.');
    } catch (error) {
      console.error('Error initializing Payphone:', error);
      toast.error('Error al inicializar el sistema de pagos');
    }
  };

  const handleBackToForm = () => {
    setShowPaymentBox(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2F3D8E] via-[#3d4d9e] to-[#4C8C3B]">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-white/80 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al inicio</span>
            </Link>
            <img 
              src="https://customer-assets.emergentagent.com/job_3ee71ab0-856c-41a8-8e43-f382a70126f5/artifacts/3okyucn0_LOGO%20ANALYTICA.png" 
              alt="Analytica" 
              className="h-10"
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-[#2F3D8E] to-[#4C8C3B] p-6 text-white">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Pago Seguro con Payphone</h1>
                <p className="text-white/80 text-sm">Visa, MasterCard, Diners Club, Discover</p>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {!showPaymentBox ? (
              /* Billing Form */
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-[#2F3D8E] mb-4">
                  <FileText className="w-5 h-5" />
                  <h2 className="text-lg font-semibold">Datos de Facturación</h2>
                </div>

                {autofilled && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3" data-testid="autofill-banner">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-[#2F3D8E]">Formulario completado automáticamente</p>
                      <p className="text-gray-600">{/* v1.12.1 · desde una factura del CRM no se habla de contrato */}
                        {/^FAC-\d{6}/.test(formData.referencia || '') ? `Hemos rellenado los datos con la información de la factura ${formData.referencia}.` : 'Hemos rellenado los datos con la información de su contrato.'} Por favor verifíquelos antes de continuar.</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nombre */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      Nombre Completo / Razón Social *
                    </label>
                    <Input
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      placeholder="Ingrese su nombre o razón social"
                      className="w-full"
                      data-testid="payphone-nombre"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Correo Electrónico *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="correo@ejemplo.com"
                      className="w-full"
                      data-testid="payphone-email"
                    />
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono Celular *
                    </label>
                    <Input
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      placeholder="0999999999 (10 dígitos, debe iniciar con 09)"
                      className={`w-full ${formData.telefono && !isValidPhoneEC(formData.telefono) ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                      data-testid="payphone-telefono"
                      maxLength={10}
                    />
                    {formData.telefono && !isValidPhoneEC(formData.telefono) && (
                      <p className="text-xs text-red-600 mt-1">Debe ser un celular ecuatoriano de 10 dígitos iniciando en 09</p>
                    )}
                    {phoneFallbackNotice && formData.telefono === PAYPHONE_FALLBACK_PHONE_EC && (
                      <p className="text-xs text-amber-700 mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        Tu número original es internacional. Se usó el celular predeterminado de Analytica ({PAYPHONE_FALLBACK_PHONE_EC}) para procesar el pago. Puedes cambiarlo si tienes un celular ecuatoriano.
                      </p>
                    )}
                  </div>

                  {/* Tipo de Identificación */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Identificación *
                    </label>
                    <select
                      name="tipoIdentificacion"
                      value={formData.tipoIdentificacion}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C8C3B]"
                      data-testid="payphone-tipo-id"
                    >
                      <option value="1">Cédula</option>
                      <option value="2">RUC</option>
                      <option value="3">Pasaporte / DNI</option>
                    </select>
                  </div>

                  {/* Identificación */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número de Identificación *
                    </label>
                    <Input
                      name="identificacion"
                      value={formData.identificacion}
                      onChange={handleInputChange}
                      placeholder="1234567890"
                      className="w-full"
                      data-testid="payphone-identificacion"
                    />
                  </div>

                  {/* Dirección */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Dirección *
                    </label>
                    <Input
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleInputChange}
                      placeholder="Calle, número, ciudad"
                      className="w-full"
                      data-testid="payphone-direccion"
                    />
                  </div>

                  {/* Monto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Monto a Pagar (USD) *
                    </label>
                    <Input
                      name="monto"
                      type="number"
                      step="0.01"
                      min="1"
                      value={formData.monto}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      className="w-full text-lg font-semibold"
                      data-testid="payphone-monto"
                    />
                  </div>

                  {/* Referencia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referencia / Concepto
                    </label>
                    <Input
                      name="referencia"
                      value={formData.referencia}
                      onChange={handleInputChange}
                      placeholder="Ej: Pago de servicio, Factura #123"
                      className="w-full"
                      data-testid="payphone-referencia"
                    />
                  </div>
                </div>

                {/* Resumen del pago con recargo Payphone */}
                {baseAmount > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-5" data-testid="payment-breakdown">
                    <h3 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Resumen del pago
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        {/* v1.12.0 · al pagar una factura del CRM (referencia FAC-…) */}
                        <span className="text-gray-600">{/^FAC-\d{6}/.test(formData.referencia || '') ? `Monto de la factura ${formData.referencia}` : 'Monto del contrato'}</span>
                        <span className="font-medium">${baseAmount.toFixed(2)}</span>
                      </div>
                      {applySurcharge && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Recargo Payphone ({PAYPHONE_SURCHARGE_PCT}%)</span>
                          <span className="font-medium text-purple-700">+${surcharge.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-purple-300 pt-2 mt-2 flex justify-between items-baseline">
                        <span className="font-semibold text-gray-900">Total a pagar</span>
                        <span className="text-2xl font-bold text-purple-700" data-testid="total-amount">
                          ${totalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    {applySurcharge ? (
                      <p className="text-[11px] text-gray-500 mt-3 italic">
                        El recargo del {PAYPHONE_SURCHARGE_PCT}% corresponde a comisiones de procesamiento Payphone. Si prefiere otro método sin recargo, contacte a Analytica.
                      </p>
                    ) : (
                      <p className="text-[11px] text-green-700 mt-3 italic font-medium">
                        ✓ Sin recargo aplicado: usted paga unicamente el monto del contrato.
                      </p>
                    )}
                  </div>
                )}

                {/* Security Badge */}
                <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Shield className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-700">
                    Pago 100% seguro procesado por <strong>Payphone</strong>. Sus datos están protegidos.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleProceedToPayment}
                  disabled={!isFormValid}
                  className="w-full bg-[#4C8C3B] hover:bg-[#3d7030] text-white py-6 text-lg font-semibold disabled:opacity-50"
                  data-testid="payphone-proceed-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Continuar al Pago
                  {baseAmount > 0 && ` - $${totalAmount.toFixed(2)}`}
                </Button>
              </div>
            ) : (
              /* Payment Box Section */
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <button 
                    onClick={handleBackToForm}
                    className="flex items-center gap-2 text-[#2F3D8E] hover:text-[#4C8C3B] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Modificar datos
                  </button>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{applySurcharge ? `Monto a pagar (incluye recargo ${PAYPHONE_SURCHARGE_PCT}%)` : 'Monto a pagar (sin recargo)'}</p>
                    <p className="text-2xl font-bold text-[#4C8C3B]">${totalAmount.toFixed(2)}</p>
                    {applySurcharge && (
                      <p className="text-[11px] text-gray-400">Base ${baseAmount.toFixed(2)} + recargo ${surcharge.toFixed(2)}</p>
                    )}
                  </div>
                </div>

                {/* Billing Summary */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <h3 className="font-semibold text-gray-700">Resumen de Facturación</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Nombre:</span>
                    <span className="font-medium">{formData.nombre}</span>
                    <span className="text-gray-500">Email:</span>
                    <span className="font-medium">{formData.email}</span>
                    <span className="text-gray-500">Identificación:</span>
                    <span className="font-medium">{formData.identificacion}</span>
                    <span className="text-gray-500">Dirección:</span>
                    <span className="font-medium">{formData.direccion}</span>
                  </div>
                </div>

                {/* Payphone Payment Box Container */}
                <div className="flex justify-center">
                  <div id="pp-button" ref={paymentBoxRef} className="w-full max-w-md"></div>
                </div>

                <p className="text-center text-sm text-gray-500">
                  Al completar el pago, recibirá una confirmación y pronto le enviaremos su factura.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PayphonePage;
